import { roleAtLeast } from '@inou/shared';

import { PageHeader } from '@/components/layout/PageHeader';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { ONBOARDING_STEPS } from '@/components/onboarding/steps';
import { StateBlock } from '@/components/reports/StateBlock';
import { TextLink } from '@/components/ui/link';
import type { OnboardingStatus } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useViewerRole } from '@/lib/viewer';

/** ヘルプへの送り先。手順はここには書かず、記事と設定画面に持たせる。 */
const GUIDES: Array<{ slug: string; label: string }> = [
  { slug: 'admin-setup', label: 'はじめての設定' },
  { slug: 'mcp-setup', label: 'MCP をつなぐ' },
  { slug: 'logging-time', label: '工数を記録する' },
];

const LEAD =
  'INOU の工数は画面ではなく、普段お使いの AI クライアントから記録します。上から順に進めると、最後は「今日は決済基盤を 3 時間」と話しかけるだけで記録が入ります。';

const READY =
  '準備はできています。AI クライアントに今日の作業を伝えてみてください。1 件でも記録が入ると、この画面は自動的に消えます。';

const STARTED =
  '最初の記録が入っています。この案内の役目は終わったので、サイドバーからは消えました。';

/**
 * 開通直後の案内 (「はじめに」)。
 *
 * この製品の主インターフェースは Web 画面ではなく AI チャットからの MCP なので、
 * 案内の終点も「画面を一通り見た」ではなく**最初の 1 件が記録された**に置く。
 *
 * **手順は書かない。** クライアント別の設定は MCP接続の画面 (`ClientSetup`) が、
 * 考え方はヘルプが持っている。ここは順番と理由だけを出してそこへ送る。
 */
export function StartPage() {
  const role = useViewerRole();
  const { data, error, loading } = useApi<OnboardingStatus>('/api/onboarding');

  /** 自分が実行できるステップだけ。他ロールの操作は最後の一言の判定に混ぜない */
  const mine = (data?.steps ?? []).filter((step) =>
    ONBOARDING_STEPS.some((view) => view.id === step.id && roleAtLeast(role, view.minRole)),
  );
  /** 全部済んだか (残りは AI に話しかけるだけ) */
  const ready = mine.length > 0 && mine.every((step) => step.done);
  /**
   * 案内が終わったテナントを、URL で開き直したとき。
   * サイドバーからは既に消えているので、ここで急かし直さない。
   */
  const started = data !== null && !data.active;

  return (
    <>
      <PageHeader title="はじめに" subtitle="最初の工数が記録されるまでの順番" />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        <p className="max-w-3xl text-[13px] text-ink-muted leading-relaxed">{LEAD}</p>

        <StateBlock loading={loading && data === null} error={error}>
          {data ? <OnboardingChecklist status={data} role={role} /> : null}
        </StateBlock>

        {started || ready ? (
          <p className="border border-line bg-surface-raised px-6 py-5 text-[13px] text-ink-muted leading-relaxed">
            {started ? STARTED : READY}
          </p>
        ) : null}

        <div className="flex flex-col gap-2 border border-line bg-surface-raised px-6 py-5">
          <span className="font-bold text-sm">{'詳しい手順'}</span>
          {GUIDES.map((guide) => (
            <TextLink key={guide.slug} to={`/help/${guide.slug}`} className="text-[13px]">
              {guide.label}
            </TextLink>
          ))}
        </div>
      </div>
    </>
  );
}
